<template>
  <q-dialog v-model="isOpen" persistent @hide="handleClose">
    <q-card style="min-width: 700px; max-width: 900px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">Component Feature Flags</div>
        <q-space />
        <q-btn icon="close"
               flat
               round
               dense
               @click="handleClose"
        />
      </q-card-section>

      <q-card-section>
        <div class="text-caption text-grey-7 q-mb-md">
          Enable or disable individual components for testing purposes. Changes are saved to localStorage.
        </div>

        <!-- Bulk Actions -->
        <div class="row q-gutter-sm q-mb-md">
          <q-btn
            label="Enable All"
            color="positive"
            size="sm"
            @click="handleEnableAll"
          />
          <q-btn
            label="Disable All"
            color="negative"
            size="sm"
            @click="handleDisableAll"
          />
          <q-btn
            label="Enable All DTP"
            color="primary"
            size="sm"
            outline
            @click="handleEnableCategory('dtp')"
          />
          <q-btn
            label="Disable All DTP"
            color="primary"
            size="sm"
            outline
            @click="handleDisableCategory('dtp')"
          />
          <q-btn
            label="Enable All IST"
            color="secondary"
            size="sm"
            outline
            @click="handleEnableCategory('ist')"
          />
          <q-btn
            label="Disable All IST"
            color="secondary"
            size="sm"
            outline
            @click="handleDisableCategory('ist')"
          />
        </div>

        <q-separator />

        <!-- DTP Components Section -->
        <div class="q-mt-md">
          <div class="text-h6 text-primary q-mb-sm">
            <q-icon name="widgets" /> DTP Components ({{ dtpCount }})
          </div>
          <div class="row q-col-gutter-sm">
            <div
              v-for="flag in dtpFlags"
              :key="flag.name"
              class="col-12 col-sm-6 col-md-4"
            >
              <q-checkbox
                :model-value="flag.enabled"
                :label="flag.name"
                color="primary"
                @update:model-value="handleToggle(flag.name)"
              />
            </div>
          </div>
        </div>

        <q-separator class="q-my-md" />

        <!-- IST Components Section -->
        <div>
          <div class="text-h6 text-secondary q-mb-sm">
            <q-icon name="extension" /> IST Components ({{ istCount }})
          </div>
          <div class="row q-col-gutter-sm">
            <div
              v-for="flag in istFlags"
              :key="flag.name"
              class="col-12 col-sm-6 col-md-4"
            >
              <q-checkbox
                :model-value="flag.enabled"
                :label="flag.name"
                color="secondary"
                @update:model-value="handleToggle(flag.name)"
              />
            </div>
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-pa-md">
        <q-btn label="Close" color="primary" @click="handleClose" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useFeatureFlags } from "../../composables/useFeatureFlags";

const {
  flags,
  dialogOpen,
  toggleFlag,
  enableAll,
  disableAll,
  enableCategory,
  disableCategory,
  closeDialog
} = useFeatureFlags();

const isOpen = computed({
  get: () => dialogOpen.value,
  set: (value) => {
    if (!value) {
      closeDialog();
    }
  }
});

const dtpFlags = computed(() =>
  flags.value.filter((f) => f.category === "dtp").sort((a, b) => a.name.localeCompare(b.name))
);

const istFlags = computed(() =>
  flags.value.filter((f) => f.category === "ist").sort((a, b) => a.name.localeCompare(b.name))
);

const dtpCount = computed(() => dtpFlags.value.length);
const istCount = computed(() => istFlags.value.length);

const handleToggle = (componentName: string) => {
  toggleFlag(componentName);
};

const handleEnableAll = () => {
  enableAll();
};

const handleDisableAll = () => {
  disableAll();
};

const handleEnableCategory = (category: "dtp" | "ist") => {
  enableCategory(category);
};

const handleDisableCategory = (category: "dtp" | "ist") => {
  disableCategory(category);
};

const handleClose = () => {
  closeDialog();
};
</script>

<style scoped lang="scss">
.q-card {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
